import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter — counts up when scrolled into view.
 * Props:
 *  - to: final numeric value
 *  - from: start value (default 0)
 *  - duration: ms (default 1400)
 *  - prefix / suffix: string decorations
 *  - decimals: fixed digits
 *  - formatter: (n) => string, overrides prefix/suffix/decimals
 *  - live: if true, ticks +delta every "step" ms (feels like a live counter)
 */
export default function AnimatedCounter({
  to,
  from = 0,
  duration = 1400,
  prefix = "",
  suffix = "",
  decimals = 0,
  formatter,
  live = false,
  liveStep = 1200,
  liveDelta = 1,
  className = "",
}) {
  const ref = useRef(null);
  const [val, setVal] = useState(from);
  const [visible, setVisible] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            obs.unobserve(el);
          }
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || startedRef.current) return;
    startedRef.current = true;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setVal(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, from, to, duration]);

  useEffect(() => {
    if (!visible || !live) return;
    const iv = setInterval(() => {
      setVal((v) => v + liveDelta * (0.6 + Math.random() * 0.8));
    }, liveStep);
    return () => clearInterval(iv);
  }, [visible, live, liveStep, liveDelta]);

  const display = formatter
    ? formatter(val)
    : `${prefix}${Number(val).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${suffix}`;

  return (
    <span ref={ref} data-testid="animated-counter" className={className}>
      {display}
    </span>
  );
}
